import Link from "next/link";
import styled from "styled-components";

import {
  Alert,
  Badge,
  Button,
  ButtonGroup,
  Form,
  Spinner,
  Table,
  ToggleButton,
} from "react-bootstrap";

import {
  AttributesView,
  Card,
  CardGrid,
  CardSiderFlexContent,
  GridForm,
  GridFormItemWrapper,
  HintIcon,
  ScrollingTextArea,
  toAttributes,
  TooltipProvider,
} from "@bagel-web/components";

import { hasValidFunctionNames } from "./playgroundFunctions";
import FunctionCall from "../chalk/FunctionCall";
import {
  FnDesc,
  PlaygroundGenerationMode,
  PlaygroundRunResult,
} from "../types";
import {
  MAX_BATCH_RUNS,
  MAX_CONCURRENT_RUNS,
  parseBatchCount,
  PlaygroundRunRecord,
} from "./playgroundRun";

const GENERATION_MODES: Array<{
  value: PlaygroundGenerationMode;
  label: string;
}> = [
  { value: "function", label: "Choose Function" },
  { value: "json", label: "JSON" },
  { value: "text", label: "Text" },
];

// size="sm" packs columns tight; give the body cells some horizontal room.
const RunsTable = styled(Table)`
  tbody td {
    padding-left: 10px;
    padding-right: 10px;
  }
`;

// What a run generated: a function call, JSON, or text.
const RunResultView = ({ result }: { result: PlaygroundRunResult }) => {
  if (result.selected_fn) {
    return (
      <FunctionCall
        selectedFn={result.selected_fn}
        showMessageWhitespace={false}
      />
    );
  }
  if (result.generated_json !== undefined && result.generated_json !== null) {
    return (
      <pre className="mb-0" style={{ whiteSpace: "pre-wrap" }}>
        {JSON.stringify(result.generated_json, null, 2)}
      </pre>
    );
  }
  if (result.generated_text != null) {
    return (
      <span style={{ whiteSpace: "pre-wrap" }}>{result.generated_text}</span>
    );
  }
  return null;
};

// One run's status badge.
const RunStatusBadge = ({ run }: { run: PlaygroundRunRecord }) => (
  <>
    {run.status === "running" && <Badge bg="secondary">Running</Badge>}
    {run.status === "ok" && <Badge bg="success">OK</Badge>}
    {run.status === "error" && (
      <TooltipProvider tooltip={run.error ?? "Run failed."}>
        <Badge bg="danger">Failed</Badge>
      </TooltipProvider>
    )}
  </>
);

// The link from a completed run to its session in Agent History. Opens in a
// new window so the run history survives.
const RunSessionLink = ({
  run,
  testId,
}: {
  run: PlaygroundRunRecord;
  testId: string;
}) =>
  run.result ? (
    <Link
      href={`/sessions/${run.result.session_id}/agent/${run.result.agent_id}`}
      target="_blank"
      data-testid={testId}
    >
      View Session <i className="bi-box-arrow-up-right" />
    </Link>
  ) : null;

// The most recent run, always shown at the top of the step — empty until
// the first run.
const LatestRunCard = ({ run }: { run: PlaygroundRunRecord | undefined }) => (
  <Card
    Sider={() => (
      <CardSiderFlexContent $collapsed={false}>
        <h5>Latest Run</h5>
      </CardSiderFlexContent>
    )}
  >
    <div
      className="d-flex flex-row gap-2 align-items-start"
      data-testid="playground-latest-run"
    >
      <ScrollingTextArea
        className="flex-grow-1"
        $maxHeight="200px"
        $resize={false}
      >
        {!run && <span className="text-body-secondary">No runs yet.</span>}
        {run?.status === "running" && <Spinner size="sm" />}
        {run?.result && <RunResultView result={run.result} />}
        {run?.status === "error" && <code>{run.error ?? "Run failed."}</code>}
      </ScrollingTextArea>
      {run && (
        <div className="text-nowrap">
          <RunStatusBadge run={run} />
        </div>
      )}
      {run && (
        <div className="text-nowrap">
          <RunSessionLink
            run={run}
            testId="playground-latest-run-session-link"
          />
        </div>
      )}
    </div>
  </Card>
);

/**
 * Step 5 — Runner.
 *
 * Run inputs are the generation mode, its schema when JSON, and the cue.
 * Moment N's input messages are folded into the seed state server-side, so
 * "what just happened" is edited in Step 4 like all other state.
 *
 * Run state (inputs and results) lives in PlaygroundPage: this step
 * unmounts on every tab switch, and an in-flight run must still land in
 * the results list.
 */
const PlaygroundRunnerStep = ({
  functions,
  cue,
  onCueChange,
  generationMode,
  onGenerationModeChange,
  jsonSchema,
  onJsonSchemaChange,
  batchEnabled,
  onBatchEnabledChange,
  batchCount,
  onBatchCountChange,
  runs,
  isRunning,
  isReplayReady,
  needsProject = false,
  apiKeySelect,
  onRun,
}: {
  functions: Array<FnDesc>;
  cue: string;
  onCueChange: (cue: string) => void;
  generationMode: PlaygroundGenerationMode;
  onGenerationModeChange: (mode: PlaygroundGenerationMode) => void;
  jsonSchema: string;
  onJsonSchemaChange: (schema: string) => void;
  // Batch mode fans the run out client-side: the count field names how
  // many requests, the page pools them.
  batchEnabled: boolean;
  onBatchEnabledChange: (enabled: boolean) => void;
  batchCount: string;
  onBatchCountChange: (count: string) => void;
  runs: Array<PlaygroundRunRecord>;
  isRunning: boolean;
  isReplayReady: boolean;
  // Runs create their session in the host's active project; true when the
  // login has none.
  needsProject?: boolean;
  // The API-key picker, supplied by the page (which owns the credential
  // state the run payload is built from) when the platform's auth path
  // requires a key; null under developer SSO.
  apiKeySelect: React.ReactNode;
  onRun: () => void;
}) => {
  // Function mode has nothing to select without functions, and the run API
  // rejects a function the Functions step has flagged.
  const needsFunctions =
    generationMode === "function" && functions.length === 0;
  const needsValidFunctionNames =
    generationMode === "function" && !hasValidFunctionNames(functions);

  // JSON generation must ship a schema, and the run request takes it as a
  // JSON object.
  let jsonSchemaError: string | null = null;
  if (generationMode === "json") {
    if (jsonSchema.trim() === "") {
      jsonSchemaError = "Provide a JSON Schema for JSON generation.";
    } else {
      try {
        const parsed: unknown = JSON.parse(jsonSchema);
        if (
          typeof parsed !== "object" ||
          parsed === null ||
          Array.isArray(parsed)
        ) {
          jsonSchemaError = "The schema must be a JSON object.";
        }
      } catch {
        jsonSchemaError = "The schema is not valid JSON.";
      }
    }
  }

  // An enabled batch must name a parseable size before Run unlocks.
  const batchCountInvalid =
    batchEnabled && parseBatchCount(batchCount) === null;

  const canRun =
    !needsFunctions &&
    !needsValidFunctionNames &&
    !jsonSchemaError &&
    !isRunning &&
    isReplayReady &&
    !needsProject &&
    !batchCountInvalid;

  // Records are appended in run order, so the last one is the newest.
  const latestRun = runs.at(-1);

  return (
    <>
      {needsFunctions && (
        <Alert variant="info" data-testid="playground-preflight-functions">
          The draft has no functions, so there is nothing for the agent to
          select. Add one in the Functions step, or pick a different generation
          mode.
        </Alert>
      )}
      {needsValidFunctionNames && (
        <Alert variant="info" data-testid="playground-preflight-function-names">
          A function has an invalid or duplicated name. Fix it in the Functions
          step, or pick a different generation mode.
        </Alert>
      )}
      {!isReplayReady && (
        <Alert variant="info" data-testid="playground-preflight-credentials">
          Runs here authenticate with an API key. Select one in Runner settings.
        </Alert>
      )}
      {needsProject && (
        <Alert variant="info" data-testid="playground-preflight-project">
          Runs create their session in your active project, and this login has
          none. Select a project first.
        </Alert>
      )}
      <div className="d-flex flex-row align-items-start">
        <div className="flex-grow-1">
          <CardGrid maxColumns={1}>
            <LatestRunCard run={latestRun} />
          </CardGrid>
        </div>
        <div
          className="d-flex flex-column align-items-end"
          style={{ marginLeft: "20px" }}
        >
          <div className="d-flex flex-row align-items-center">
            {batchEnabled && (
              <Form.Control
                type="number"
                min={1}
                max={MAX_BATCH_RUNS}
                value={batchCount}
                isInvalid={batchCountInvalid}
                onChange={(event) => onBatchCountChange(event.target.value)}
                aria-label="Batch Count"
                className="me-2"
                style={{ width: "90px" }}
                data-testid="playground-batch-count"
              />
            )}
            <TooltipProvider
              tooltip={
                batchEnabled
                  ? `Run the draft in fresh playground sessions, up to ${MAX_CONCURRENT_RUNS} at a time.`
                  : "Run the draft in a fresh playground session."
              }
            >
              {/* span keeps the tooltip working over a disabled button */}
              <span style={{ display: "inline-flex" }}>
                <Button
                  disabled={!canRun}
                  onClick={onRun}
                  data-testid="playground-run-button"
                >
                  {isRunning ? (
                    <Spinner size="sm" className="me-1" />
                  ) : (
                    <i className="bi-play-fill me-1" />
                  )}
                  {batchEnabled ? "Run Batch" : "Run"}
                </Button>
              </span>
            </TooltipProvider>
          </div>
          <Form.Check
            type="switch"
            id="playground-batch-switch"
            label="Batch Runs"
            className="mt-2 text-nowrap"
            checked={batchEnabled}
            onChange={(event) => onBatchEnabledChange(event.target.checked)}
            data-testid="playground-batch-switch"
          />
        </div>
      </div>
      <CardGrid maxColumns={1}>
        <Card title="Runner settings">
          <GridForm $width="100%" onSubmit={(event) => event.preventDefault()}>
            <GridFormItemWrapper>
              <Form.Label className="d-block">Generation Mode</Form.Label>
              <ButtonGroup aria-label="Generation Mode">
                {GENERATION_MODES.map(({ value, label }) => (
                  <ToggleButton
                    key={value}
                    type="radio"
                    name="playground-generation-mode"
                    id={`playground-generation-mode-${value}`}
                    value={value}
                    variant="outline-secondary"
                    checked={generationMode === value}
                    onChange={() => onGenerationModeChange(value)}
                  >
                    {label}
                  </ToggleButton>
                ))}
              </ButtonGroup>
            </GridFormItemWrapper>
            {apiKeySelect && (
              <GridFormItemWrapper>
                <Form.Label>API Key</Form.Label>
                {apiKeySelect}
              </GridFormItemWrapper>
            )}
            {generationMode === "json" && (
              <GridFormItemWrapper colSpan={2}>
                <Form.Group controlId="playground-runner-json-schema-field">
                  <Form.Label>JSON Schema</Form.Label>
                  <Form.Control
                    as="textarea"
                    className="font-monospace"
                    style={{ height: "100px" }}
                    value={jsonSchema}
                    isInvalid={!!jsonSchemaError}
                    onChange={(event) => onJsonSchemaChange(event.target.value)}
                    data-testid="playground-runner-json-schema"
                  />
                  {jsonSchemaError && (
                    <div
                      className="invalid-feedback d-block"
                      data-testid="playground-runner-json-schema-invalid"
                    >
                      {jsonSchemaError}
                    </div>
                  )}
                </Form.Group>
              </GridFormItemWrapper>
            )}
            <GridFormItemWrapper colSpan={2}>
              <Form.Group controlId="playground-runner-cue-field">
                <Form.Label>Cue</Form.Label>
                <Form.Control
                  as="textarea"
                  style={{ height: "100px" }}
                  value={cue}
                  onChange={(event) => onCueChange(event.target.value)}
                  data-testid="playground-runner-cue"
                />
              </Form.Group>
            </GridFormItemWrapper>
          </GridForm>
        </Card>
      </CardGrid>
      {latestRun && (
        <CardGrid maxColumns={1} className="mt-3">
          <Card title="Run History">
            <RunsTable striped size="sm" data-testid="playground-run-results">
              <thead>
                <tr>
                  <th>Started</th>
                  <th>Cue</th>
                  <th>Agent LLM</th>
                  <th>Result</th>
                  <th>Status</th>
                  <th>Details</th>
                </tr>
              </thead>
              <tbody>
                {runs.map((run) => (
                  <tr key={run.id} data-testid={`playground-run-row-${run.id}`}>
                    <td className="text-nowrap">
                      {new Date(run.startedAt).toLocaleTimeString()}
                    </td>
                    <td>
                      {run.cue ?? (
                        <span className="text-body-secondary">—</span>
                      )}
                    </td>
                    <td className="text-nowrap">
                      {run.agentLlm ? (
                        <>
                          <code>{run.agentLlm}</code>
                          {Object.keys(run.agentLlmParams).length > 0 && (
                            <span
                              data-testid={`playground-run-llm-params-${run.id}`}
                            >
                              <HintIcon variant="info">
                                <AttributesView
                                  truncateValues={false}
                                  attributes={toAttributes(run.agentLlmParams)}
                                />
                              </HintIcon>
                            </span>
                          )}
                        </>
                      ) : (
                        <span className="text-body-secondary">—</span>
                      )}
                    </td>
                    <td>
                      {run.result ? (
                        <RunResultView result={run.result} />
                      ) : (
                        <span className="text-body-secondary">
                          {run.status === "error" ? run.error : "—"}
                        </span>
                      )}
                    </td>
                    <td>
                      <RunStatusBadge run={run} />
                    </td>
                    <td className="text-nowrap">
                      <RunSessionLink
                        run={run}
                        testId={`playground-run-session-link-${run.id}`}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </RunsTable>
          </Card>
        </CardGrid>
      )}
    </>
  );
};

export default PlaygroundRunnerStep;
